import { Box, Typography } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridEventListener,
  GridFooter,
  GridFooterContainer,
  GridRowsProp,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueFormatterParams,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useGetCustomerPagesQuery,
  useLazyGetCustomerPagesQuery
} from '../../redux/api/independentApi'
import { CustomerPages, RepairPaymentEntry } from '../../sdk'
import { formatCurrency } from '../../utils'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import { SearchWithAutocomplete } from '../SearchWithAutocomplete/SearchWithAutocomplete'

const PAGE_SIZE = 1000

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <div style={{ flexGrow: 1 }} />
      <SearchWithAutocomplete />
    </GridToolbarContainer>
  )
}

const CustomFooter = ({ reachedEnd }: { reachedEnd: boolean }) => {
  return (
    <GridFooterContainer>
      <Box pl={2}>
        {reachedEnd ? (
          <Typography>All Results Loaded</Typography>
        ) : (
          <Typography>Loading Remaining Rows...</Typography>
        )}
      </Box>
      <div style={{ flexGrow: 1 }} />
      <GridFooter />
    </GridFooterContainer>
  )
}

const CustomFooterAdd = () => {
  return <CustomFooter reachedEnd={false} />
}

const CustomFooterAll = () => {
  return <CustomFooter reachedEnd={true} />
}

const CustomerList: FC = () => {
  const navigate = useNavigate()
  const gridApiRef = useGridApiRef()

  const { gridBoxHeight } = useContext(AppContext)

  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [triggerSearch] = useLazyGetCustomerPagesQuery()

  // fetch initial entries and display first
  const { data: initial, isLoading } = useGetCustomerPagesQuery({
    authorization: accessToken,
    shop: shopId,
    maxTimestampExclusive: '9999-12-31T23:59:59+00',
    numResults: PAGE_SIZE
  })

  const initialCustomerPages = useMemo(() => initial || [], [initial])

  const [remainingCustomerPages, setRemainingCustomerPages] = useState<
    CustomerPages[]
  >([])

  useEffect(() => {
    const fetchData = async () => {
      // only triggered when initialCustomerPages is changed
      if (initialCustomerPages.length > 0) {
        let remaining: CustomerPages[] = []

        // fetch remaining entries and add only after all fetched in background
        while (true) {
          // note that initialCustomerPages length is at least 1 here
          const endDateTime =
            remaining.length > 0
              ? remaining[
                  remaining.length - 1
                ].customer?.createTime!.toISOString()
              : initialCustomerPages[
                  initialCustomerPages.length - 1
                ].customer?.createTime!.toISOString()

          const next = await triggerSearch({
            authorization: accessToken,
            shop: shopId,
            maxTimestampExclusive: endDateTime,
            numResults: PAGE_SIZE
          }).unwrap()

          remaining = [...remaining, ...next]

          if (next.length < 2) {
            break
          }
        }

        setRemainingCustomerPages(remaining)
      }
    }

    fetchData()
  }, [accessToken, initialCustomerPages, shopId, triggerSearch])

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'firstname',
        headerName: 'First Name',
        flex: 1
      },
      {
        field: 'lastname',
        headerName: 'Last Name',
        flex: 1
      },
      {
        field: 'companyName',
        headerName: 'Company Name',
        flex: 1
      },
      {
        field: 'phone',
        headerName: 'Phone Number',
        flex: 1
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 2
      },
      {
        type: 'date',
        field: 'created',
        headerName: 'Created Date',
        flex: 1,

        valueFormatter: (params: GridValueFormatterParams) =>
          formatMdyDate(params.value as Date)
      },
      {
        field: 'spent',
        headerName: 'Dollars Spent',
        flex: 1,
        valueFormatter: (params: GridValueFormatterParams) =>
          formatCurrency(params.value as number, true)
      },
      {
        field: 'numVehicles',
        headerName: '# Vehicles',
        flex: 1
      }
    ]
  }, [])

  const rows: GridRowsProp = useMemo(() => {
    const customerPages = [...initialCustomerPages, ...remainingCustomerPages]

    return (customerPages || []).map((entry) => ({
      id: entry.customer?.id,
      firstname: entry.customer?.information?.firstname,
      lastname: entry.customer?.information?.lastname,
      companyName: entry.customer?.information?.companyName,
      phone: entry.customer?.information?.phone,
      email: entry.customer?.information?.email,
      created: entry.customer?.createTime,
      spent: entry.payments?.reduce(
        (prev: number, current: RepairPaymentEntry) => {
          return prev + current.info!.amount!
        },
        0
      ),
      numVehicles: entry.vehicles?.length ? entry.vehicles?.length : 0
    }))
  }, [initialCustomerPages, remainingCustomerPages])

  useEffect(() => {
    const onRowClick: GridEventListener<'rowClick'> = ({ id }) => {
      const singleCustomerUrl = generatePath(AppRoutes.CUSTOMER_DETAILS, {
        id: id as string
      })
      navigate(singleCustomerUrl)
    }

    return gridApiRef.current.subscribeEvent('rowClick', onRowClick)
  }, [gridApiRef, navigate])

  return (
    <Box sx={{ height: gridBoxHeight, width: '100%' }}>
      <DataGridPro
        apiRef={gridApiRef}
        density="compact"
        rows={rows}
        columns={columns}
        loading={isLoading}
        slots={{
          toolbar: CustomToolbar,
          footer:
            remainingCustomerPages.length >= 1
              ? CustomFooterAll
              : CustomFooterAdd
        }}
        sx={{
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer'
          },
          border: '0px'
        }}
      />
    </Box>
  )
}

export default CustomerList
